import { createContext, useContext, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

// Lets a routed page (the editor) render its title + save status inside
// the shell's top bar, and veto in-app navigation away from it (the brand
// link back to Home) while it has unsaved changes.

interface ShellHeaderContextValue {
    slot: HTMLElement | null;
    setLeaveGuard: (guard: (() => boolean | Promise<boolean>) | null) => void;
}

export const ShellHeaderContext = createContext<ShellHeaderContextValue>({
    slot: null,
    setLeaveGuard: () => {}
});

export function ShellHeaderPortal({ children }: { children: ReactNode }) {
    const { slot } = useContext(ShellHeaderContext);
    return slot ? createPortal(children, slot) : null;
}

/** `guard` resolves true when it's OK to leave the page (it may ask the user: Save / Don't save / Cancel). */
export function useShellLeaveGuard(guard: () => boolean | Promise<boolean>) {
    const { setLeaveGuard } = useContext(ShellHeaderContext);
    useEffect(() => {
        setLeaveGuard(guard);
        return () => setLeaveGuard(null);
    }, [guard, setLeaveGuard]);
}
